import React from 'react';
import { StacklineColor, SlStatusChip, useStacklineTheme } from '@stackline/ui';
import {
  Direction,
  getColorForMetricChangeValue,
  getIconForMetricChangeForDirection
} from 'src/components/BeaconV2Components/utils/chartStyles';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import LegendIndicator, { LegendIndicatorProps } from 'src/components/BeaconV2Components/Legend/LegendIndicator';

export interface LegendWithMetricProps {
  /**
   * Main metric to be displayed
   */
  metric?: string;
  /**
   * Legend color
   */
  color: StacklineColor;
  /**
   * Display name for the legend
   */
  displayName?: string;
  /**
   * Optional metric for the change.
   */
  change?: string;
  /**
   * Indicates if the change is increasing or decreasing
   */
  direction?: Direction;
  /**
   * Override for the percent change chip and icon. Use a theme color key (e.g. 'secondary')
   * so SlStatusChip receives a valid key. The icon receives the resolved hex from the theme.
   */
  changeColorOverride?: StacklineColor;

  dashed?: boolean;

  legendIndicatorProps?: Partial<LegendIndicatorProps>;
}

/**
 * Displays a metric with a legend underneath, and an optional
 * status pill indicating the change from the previous period
 */
const LegendWithMetric = ({
  metric,
  change,
  direction,
  color,
  displayName,
  changeColorOverride,
  dashed,
  legendIndicatorProps
}: LegendWithMetricProps) => {
  const theme = useStacklineTheme();
  const changeChipColor = changeColorOverride ?? getColorForMetricChangeValue(null, direction);
  const resolvedIconColor = changeColorOverride ? theme.colors[changeColorOverride] : undefined;
  const changeIcon = getIconForMetricChangeForDirection(direction, resolvedIconColor);

  return (
    <Flex gap="sm" flexDirection="column">
      {(change || metric) && (
        <Flex gap="md">
          {metric && <Text variant="body0">{metric}</Text>}
          {change && (
            <SlStatusChip
              color={(changeChipColor as React.ComponentProps<typeof SlStatusChip>['color'])}
              variant="xlarge"
            >
              <Flex alignItems="center" gap="xs">
                <div
                  style={{
                    position: 'relative',
                    top: '1px'
                  }}
                >
                  {changeIcon}
                </div>
                {change}
              </Flex>
            </SlStatusChip>
          )}
        </Flex>
      )}
      <LegendIndicator color={color} displayName={displayName} dashed={dashed} {...legendIndicatorProps} />
    </Flex>
  );
};

export default LegendWithMetric;
